<template lang="pug">
div
  slot(name="title")
  v-form(ref="form")
    div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('service_type') }}
    y-select.mt-2(v-model="form.service" :items="services" :placeholder="$t('select_service_type')" :rules="rules.not_empty_select" :loading="isFetchingService" @change="fetchProviders(form.service)")
    div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('doctor') }}
    y-select.mt-2(v-model="form.provider" :items="providers" :placeholder="$t('select_doctor')" :rules="rules.not_empty_select" :loading="isFetchingProvider" no-data-text="No provider available" @change="clearDateTime(form.provider)")
    div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('appointment_date') }}
    y-dialog(v-model='showPicker' width='320px' persistent)
      template(v-slot:activator='{ on }')
        v-text-field.text-sm.mt-2.round-xl(v-model="selectedDateField" :placeholder="$t('select_appointment_date')" outlined :rules="rules.not_empty" hide-details="auto" v-on="on" :disabled="!form.provider" :loading="isFetchingDates")
          template(v-slot:append)
            eva-icon(name="calendar-outline" fill="#848484")
      v-card
        v-card-text.px-2.pt-2.pb-0
          div.d-flex.justify-center.align-center(v-if="isFetchingDates" style="height: 380px;")
            v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
          yc-date-picker(v-else :parentDate.sync="selectedDate" color="info" :allowedDates="unavailableDateList" :minDate="getMinDate()" :parentPickerDate.sync="pickerDate")
        v-card-actions.px-4.pb-4
          v-row(no-gutters)
            v-col.pr-2
              v-btn.button-sm(color="basic700" block dark outlined depressed @click="closeDate") {{ $t('cancel') }}
            v-col.pl-2
              v-btn.button-sm(color="info500" block dark depressed :dark="Boolean(selectedDate)" :disabled="!selectedDate" @click="pickDate(form.service, form.provider, selectedDate)") {{ $t('select') }}

    div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('available_time') }}
    y-select.mt-2(v-model="form.time" :items="times" item-text="text" item-value="value" :placeholder="$t('select_available_time')" :rules="rules.not_empty_select" :loading="isFetchingTimes" no-data-text="No timeslot available")
    div.text-sm.font-semibold.neutral500--text.mt-4 {{ $t('notes') }}
    v-textarea.text-sm.mt-2.round-xl(v-model="form.remarks" :placeholder="$t('notes_optional')" outlined hide-details="auto")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'

export default {
  name: 'AppointmentDetails',
  props: ['appointment'],
  data () {
    return {
      isFetchingService: false,
      isFetchingProvider: false,
      isFetchingDates: false,
      isFetchingTimes: false,
      showPicker: false,
      form: {},
      services: [],
      providers: [],
      unavailableDates: [],
      times: [],
      selectedDate: null,
      selectedDateField: null,
      selectedService: null,
      pickerDate: null,
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
    }),
    detailsData () {
      return {
        form: this.form,
        service: this.selectedService
      }
    }
  },
  watch: {
    detailsData: {
      deep: true,
      handler (val) {
        this.$store.commit('clinic/updateTempAppointment', val)
      } 
    },
    pickerDate (val) {
      this.fetchUnavailableDates(this.form.provider, val)
    }
  },
  mounted () {
    if (this.appointment) {
      this.setData()
    } else {
      this.fetchServices()
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      this.form = {
        service: this.appointment.service.id,
        provider: this.appointment.provider.id,
        date: this.appointment.date,
        time: this.appointment.time,
        remarks: this.appointment.lead.remarks
      }
      this.selectedDateField = this.$options.filters.humanizeDate(this.form.date)
      this.isFetchingService = true
      this.isFetchingProvider = true
      this.isFetchingTimes = true
      this.$axios.get(`/clinics/${this.company.slug}/services?page_size=-1&fields=id,name`)
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.services = resp.data.results
            if (!this.services.find(f => f.id === this.form.service)) {
              this.services.unshift({ name: this.appointment.service.name, id: this.appointment.service.id })
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingService = false
        })
      this.$axios.get(`/clinics/${this.company.slug}/providers?page_size=-1&services=${this.form.service}&status=active&fields=id,name`)
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.providers = resp.data.results
            if (!this.providers.find(f => f.id === this.form.provider)) {
              this.providers.unshift({ name: this.appointment.provider.name, id: this.appointment.provider.id })
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingProvider = false
        })
      this.$axios.get(`/clinics/${this.company.slug}/providers/${this.form.provider}/available_times?service=${this.form.service}&date=${this.form.date}`)
        .then(resp => {
          this.times = resp.data.times.map(m => { return { text: this.$options.filters.displayTime2(m), value: m }})
          if (!this.times.find(f => f.value === this.form.time)) {
              this.times.unshift({ text: this.$options.filters.displayTime2(this.appointment.time), value: this.appointment.time })
            }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingTimes = false
        })
    },
    fetchServices () {
      this.isFetchingService = true
      this.$axios.get(`/clinics/${this.company.slug}/services?page_size=-1&fields=id,name`)
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.services = resp.data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingService = false
        })
    },
    fetchServicesInfo (val) {
      this.$axios.get(`/clinics/${this.company.slug}/services/${val}`)
        .then(resp => {
          this.selectedService = resp.data
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    fetchProviders (service) {
      this.fetchServicesInfo(service)
      this.isFetchingProvider = true
      this.providers = []
      this.unavailableDates = []
      this.times = []
      !this.form.provider || this.$set(this.form, 'provider', null)
      !this.form.date || this.$set(this.form, 'date', null)
      !this.form.time || this.$set(this.form, 'time', null)
      this.selectedDate = null
      this.selectedDateField = null
      this.$axios.get(`/clinics/${this.company.slug}/providers?page_size=-1&services=${service}&status=active&fields=id,name`)
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.providers = resp.data.results
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingProvider = false
        })
    },
    fetchUnavailableDates (provider, yearMonth) {
      this.isFetchingDates = true
      let params = {}
      if (yearMonth) {
        params.year = yearMonth.substr(0, 4),
        params.month = yearMonth.substr(-2)
      }
      this.$axios.get(`/clinics/${this.company.slug}/providers/${provider}/leave_dates`, { params })
        .then(resp => {
          this.unavailableDates = resp.data
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingDates = false
        })
    },
    fetchTimes (service, provider, date) {
      this.isFetchingTimes = true
      !this.form.time || this.$set(this.form, 'time', null)
      this.$axios.get(`/clinics/${this.company.slug}/providers/${provider}/available_times?service=${service}&date=${date}`)
        .then(resp => {
          this.times = resp.data.times.map(m => { return { text: this.$options.filters.displayTime2(m), value: m }})
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingTimes = false
        })
    },
    unavailableDateList (val) {
      if (this.unavailableDates.includes(val)) {
        return false
      }
      return true
    },
    pickDate (service, provider, date) {
      this.selectedDateField = this.$options.filters.humanizeDate(date)
      this.$set(this.form, 'date', date)
      this.showPicker = false
      this.fetchTimes(service, provider, date)
    },
    closeDate () {
      this.showPicker = false
    },
    clearDateTime (provider) {
      this.times = []
      !this.form.date || this.$set(this.form, 'date', null)
      !this.form.time || this.$set(this.form, 'time', null)
      this.selectedDate = null
      this.selectedDateField = null
      this.fetchUnavailableDates(provider, this.pickerDate)
    },
    getMinDate () {
      if (this.selectedService && (this.selectedService.preparation_time_unit === 'days' || this.selectedService.preparation_time_unit === 'weeks' || this.selectedService.preparation_time_unit === 'months')) {
        let days = 0
        let weeks = 0
        let months = 0
        if (this.selectedService.preparation_time_unit === 'days') {
          days = this.selectedService.preparation_time_value
        } else if (this.selectedService.preparation_time_unit === 'weeks') {
          weeks = this.selectedService.preparation_time_value
        } else if (this.selectedService.preparation_time_unit === 'months') {
          months = this.selectedService.preparation_time_value
        }
        let date = DateTime.local()
        date = date.plus({days: days, weeks: weeks, months: months})
        return date.toFormat('yyyy-MM-dd')
      } else {
        const today = new Date()
        const year = today.getFullYear()
        const month = String(today.getMonth() + 1).padStart(2, '0')
        const day = String(today.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`;
      }
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>